import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AppFile,
  Buyer,
  DocumentsSummary,
  QuickbooksInvoice,
  SupplierForBuyer,
} from '@dill/dill-shared';
import { canUserAccessBuyerBranch } from '@dill/dill-shared';
import { File, Paperclip, X } from 'phosphor-react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../redux/services/suppliersService';
import { getGroupedSuppliersInvoices } from '../../../../redux/services/invoicesService';
import { createDispute } from '../../../../redux/services/disputesService';
import { DillModal, DillButton, DillInput, DillField, DillSelect } from '../../../general/Dill';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';

/**
 * Lets a supplier user file a dispute directly against a customer's invoice, rather than
 * waiting for one to appear as a byproduct of a buyer short-paying on the AR portal.
 *
 * Opened from two contexts with the same shape: from an invoice row (buyerId + invoice both
 * known and locked) and from the Disputes tab itself (nothing known yet, so the customer and
 * invoice are picked here).
 */

interface PreselectedInvoice {
  id: string;
  docNumber?: string | null;
  salesRep?: string | null;
}

interface CreateDisputeModalProps {
  open: boolean;
  onClose: () => void;
  buyerId?: string | null;
  invoice?: PreselectedInvoice | null;
  onCreated?: () => void;
}

export const CreateDisputeModal = ({
  open,
  onClose,
  buyerId: fixedBuyerId,
  invoice: fixedInvoice,
  onCreated,
}: CreateDisputeModalProps) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const user = useAppSelector((state) => state.auth.user);
  const { customers } = useAppSelector((state) => state.supplier);
  const { loadingList } = useAppSelector((state) => state.disputes);
  const supplierId = currentSupplier?.id;

  const [buyerId, setBuyerId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [buyerInvoices, setBuyerInvoices] = useState<QuickbooksInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerCount, setCustomerCount] = useState(0);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [reasonOther, setReasonOther] = useState('');
  const [disputeNotes, setDisputeNotes] = useState('');
  const [attachments, setAttachments] = useState<AppFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectableCustomers = useMemo(
    () =>
      customers.filter((customerDetails) =>
        canUserAccessBuyerBranch({
          user,
          supplierId,
          buyerBranch: customerDetails?.buyer?.branch,
        })
      ),
    [customers, user, supplierId]
  );

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: any[];
      documentsSummary: DocumentsSummary;
    }[]
  ) => {
    if (firstCustomers.length > count || !supplierId) {
      return;
    }
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      const results = await dispatch(
        recursiveGetUserCustomers({
          supplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled' && results.payload) {
        const payload = results.payload as any;
        const fetchedCustomers = (payload.data ?? []) as unknown[];
        const lastDocumentId = payload.lastDocumentId;
        if (lastDocumentId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDocumentId);
          currentPreviousLastDocumentId = lastDocumentId;
        }
        if (!lastDockId || fetchedCustomers.length < pageSize) {
          break;
        }
      } else {
        break;
      }
    }
  };

  const fetchCustomers = async () => {
    if (!supplierId) {
      return;
    }
    setLoadingCustomers(true);
    const pageSize = 100;
    try {
      const results = await dispatch(
        getUserCustomers({
          supplierId,
          previousLastDocumentId: '',
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      if (results.type === 'supplier/getUserCustomers/fulfilled' && results.payload) {
        const payload = results.payload as any;
        const cust = (payload.data ?? []) as any[];
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== previousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);
          await recursiveFetchCustomers(Number(payload.customerCount), lastDockId, [...cust]);
        }
      }
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchBuyerInvoices = async (forBuyerId: string) => {
    if (!forBuyerId || !supplierId) {
      setBuyerInvoices([]);
      return;
    }
    setLoadingInvoices(true);
    try {
      const results = await dispatch(
        getGroupedSuppliersInvoices({ buyerId: forBuyerId, weeksLimit: 52, supplierId })
      );
      if (results && results.type === 'suppliers/getGroupedSuppliersInvoices/fulfilled') {
        const payload = results.payload as any;
        if (payload?.data?.buyerSuppliers) {
          const data = payload.data.buyerSuppliers as SupplierForBuyer[];
          let invoices: QuickbooksInvoice[] = [];
          data.forEach((item) => {
            invoices = [...invoices, ...(item.invoices?.pastDueInvoices.invoices || [])];
            invoices = [...invoices, ...(item.invoices?.currentInvoices.invoices || [])];
            invoices = [...invoices, ...(item.invoices?.pendingPayInvoices.invoices || [])];
          });
          setBuyerInvoices(invoices);
          return;
        }
      }
      setBuyerInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Reset on each open, and seed whatever the caller already knows.
  useEffect(() => {
    if (!open) {
      return;
    }
    const initialBuyerId = fixedBuyerId || '';
    setBuyerId(initialBuyerId);
    setInvoiceId(fixedInvoice?.id || '');
    setBuyerInvoices([]);
    setDisputeReason('');
    setReasonOther('');
    setDisputeNotes('');
    setAttachments([]);
    if (!fixedBuyerId) {
      fetchCustomers();
    } else if (!fixedInvoice) {
      fetchBuyerInvoices(initialBuyerId);
    }
    // Depend on the invoice's id, not the `fixedInvoice` object itself — a caller that rebuilds
    // that object on every render (a new literal with the same id) would otherwise re-fire this
    // reset each time and repeatedly wipe out whatever the user had already picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedBuyerId, fixedInvoice?.id]);

  const customerOptions = useMemo(
    () =>
      selectableCustomers
        .map((customerDetails) => ({
          value: customerDetails.buyer.id,
          label: customerDetails.buyer.name || customerDetails.buyer.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [selectableCustomers]
  );

  const invoiceOptions = useMemo(
    () =>
      buyerInvoices.map((invoiceRow) => ({
        value: invoiceRow.id,
        label: `${invoiceRow.docNumber || invoiceRow.id}${
          invoiceRow.balance != null ? ` — $${Number(invoiceRow.balance).toFixed(2)} open` : ''
        }`,
      })),
    [buyerInvoices]
  );

  const reasonOptions = useMemo(() => {
    const configured = currentSupplier?.partialPaymentReasons || [];
    // "Tax Exemption" and "Pricing" are always offered, even for a supplier who hasn't added
    // them to their own reason list yet, since a Dispute Management routing rule may target
    // either regardless of whether the supplier has customized this list.
    const allReasons = Array.from(new Set([...configured, 'Tax Exemption', 'Pricing', 'Other']));
    return allReasons.map((reason) => ({ value: reason, label: reason }));
  }, [currentSupplier?.partialPaymentReasons]);

  const handleBuyerChange = useCallback((value: string) => {
    setBuyerId(value);
    setInvoiceId('');
    setBuyerInvoices([]);
    fetchBuyerInvoices(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !supplierId) {
      return;
    }
    setUploading(true);
    const blobUrl = URL.createObjectURL(file);
    try {
      const url = await firebaseLargeFileUploadHelper({
        blobUrl,
        storagePath: `disputes/${supplierId}/${buyerId || 'unassigned'}`,
        fileNameWithExtension: `${Date.now()}_${file.name}`,
      });
      if (!url) {
        openDillToast({ message: `Failed to upload ${file.name}.`, type: 'ERROR' });
        return;
      }
      setAttachments((previous) => [...previous, { url, name: file.name }]);
    } finally {
      URL.revokeObjectURL(blobUrl);
      setUploading(false);
    }
  };

  const handleRemoveAttachment = (url: string) => {
    setAttachments((previous) => previous.filter((attachment) => attachment.url !== url));
  };

  const selectedInvoice =
    fixedInvoice || buyerInvoices.find((invoiceRow) => invoiceRow.id === invoiceId);
  const canSave =
    !!buyerId &&
    !!disputeReason &&
    (disputeReason !== 'Other' || !!reasonOther.trim()) &&
    !saving &&
    !uploading;

  const handleSave = async () => {
    if (!supplierId || !canSave) {
      return;
    }
    setSaving(true);
    try {
      const result = await dispatch(
        createDispute({
          supplierId,
          buyerId,
          invoiceId: fixedInvoice?.id || invoiceId || null,
          invoiceNumber: selectedInvoice?.docNumber || null,
          invoiceSalesRepName: selectedInvoice?.salesRep || null,
          disputeReason: disputeReason === 'Other' ? reasonOther.trim() : disputeReason,
          disputeNotes: disputeNotes.trim() || null,
          disputeAttachments: attachments,
        })
      );
      if (result.meta.requestStatus === 'fulfilled' && (result.payload as any)?.success) {
        openDillToast({ message: 'Dispute created.', type: 'SUCCESS' });
        onCreated?.();
        onClose();
      } else {
        openDillToast({ message: 'Failed to create dispute.', type: 'ERROR' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title="New Dispute"
      subtitle="File a dispute against a customer's invoice."
      maxWidth="560px"
      footer={
        <>
          <DillButton onClick={onClose} label="Cancel" variant="ghost" />
          <DillButton
            onClick={handleSave}
            label={saving ? 'Creating...' : 'Create dispute'}
            variant="primary"
            isLoading={saving || loadingList.includes('createDispute')}
            isDisabled={!canSave}
          />
        </>
      }>
      <div className="flex flex-col gap-3">
        {fixedBuyerId ? null : (
          <DillField label="Customer" required>
            <DillSelect
              value={buyerId}
              onChange={handleBuyerChange}
              options={customerOptions}
              searchable
              searchPlaceholder="Search customers..."
              placeholder={
                loadingCustomers && customerOptions.length === 0
                  ? 'Loading customers...'
                  : 'Select a customer'
              }
            />
          </DillField>
        )}

        {fixedInvoice ? (
          <DillField label="Invoice">
            <div className="flex items-center h-9 px-3 rounded-lg border border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-800">
                {fixedInvoice.docNumber || fixedInvoice.id}
              </span>
            </div>
          </DillField>
        ) : (
          <DillField label="Invoice (optional)">
            <DillSelect
              value={invoiceId}
              onChange={setInvoiceId}
              options={invoiceOptions}
              searchable
              searchPlaceholder="Search invoices..."
              placeholder={
                !buyerId
                  ? 'Select a customer first'
                  : loadingInvoices
                  ? 'Loading invoices...'
                  : 'Select an invoice'
              }
              disabled={!buyerId}
            />
          </DillField>
        )}

        <DillField label="Reason" required>
          <DillSelect
            value={disputeReason}
            onChange={setDisputeReason}
            options={reasonOptions}
            placeholder="Select a reason"
          />
        </DillField>

        {disputeReason === 'Other' && (
          <DillField label="Describe the reason" required>
            <DillInput
              value={reasonOther}
              onChange={setReasonOther}
              placeholder="Describe the reason"
            />
          </DillField>
        )}

        <DillField label="Notes">
          <textarea
            value={disputeNotes}
            onChange={(event) => setDisputeNotes(event.target.value)}
            rows={3}
            placeholder="Optional — add any additional context"
            className="w-full px-3 py-2 text-sm text-gray-800 bg-white border border-gray-200 rounded-lg resize-y placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
          />
        </DillField>

        <DillField label="Attachments">
          <div className="flex flex-col gap-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.url}
                className="flex items-center justify-between gap-2 h-9 px-3 rounded-lg border border-gray-300 bg-white">
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 truncate">
                  <File size={12} />
                  {attachment.name}
                </a>
                <button
                  onClick={() => handleRemoveAttachment(attachment.url || '')}
                  className="text-gray-400 hover:text-red-500 transition-colors shrink-0">
                  <X size={14} />
                </button>
              </div>
            ))}
            <label className="inline-flex items-center gap-1.5 h-9 px-3 self-start text-xs font-medium text-blue-600 border border-blue-200 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
              <Paperclip size={14} />
              {uploading ? 'Uploading...' : 'Attach a file'}
              <input
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </label>
          </div>
        </DillField>
      </div>
    </DillModal>
  );
};

export default CreateDisputeModal;
